import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Calendar, PlusCircle, Upload, Activity, ChevronRight } from "lucide-react";

function ageFromBirth(iso) {
    if (!iso) return null;
    try {
        const b = new Date(iso);
        if (Number.isNaN(b.getTime())) return null;
        const now = new Date();
        let a = now.getFullYear() - b.getFullYear();
        const m = now.getMonth() - b.getMonth();
        if (m < 0 || (m === 0 && now.getDate() < b.getDate())) a--;
        return a;
    } catch { return null; }
}

function fmtDate(iso) {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
    } catch { return null; }
}

export default function PatientHeaderBar({ snapshot, onNewConsult, onNewEvolution, onAttachExam, isProfessional }) {
    if (!snapshot?.header?.patient) return null;
    const { patient, allergies, tags, last_consultation, next_consultation, consultation_count } = snapshot.header;
    const age = ageFromBirth(patient.birth_date);
    const initials = (patient.full_name || "?")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0]?.toUpperCase())
        .join("");

    return (
        <div
            className="sticky top-0 z-30 -mx-4 md:-mx-6 lg:-mx-8 px-4 md:px-6 lg:px-8 py-3 bg-[var(--bg-primary)]/95 backdrop-blur border-b border-[var(--border-organic)]"
            data-testid="patient-header-bar"
        >
            <div className="flex flex-wrap items-center gap-3">
                {/* Avatar + Nome */}
                <div className="flex items-center gap-3 min-w-0">
                    <div className="w-12 h-12 rounded-full bg-brand text-[var(--bg-primary)] flex items-center justify-center font-display text-lg font-medium shrink-0">
                        {initials || "?"}
                    </div>
                    <div className="min-w-0">
                        <p className="font-display text-lg font-medium text-brand line-clamp-1" data-testid="header-patient-name">
                            {patient.full_name}
                        </p>
                        <p className="text-xs text-muted-clinical flex items-center gap-2 flex-wrap">
                            {age != null && <span>{age} anos</span>}
                            {patient.sex && <span>· {patient.sex === "M" ? "Masculino" : patient.sex === "F" ? "Feminino" : patient.sex}</span>}
                            {patient.insurance && <span>· {patient.insurance}</span>}
                            {consultation_count > 0 && <span>· {consultation_count} consulta{consultation_count > 1 ? "s" : ""}</span>}
                        </p>
                    </div>
                </div>

                {/* Alergias em vermelho — sempre visível */}
                {allergies && (
                    <div
                        className="flex items-center gap-2 px-3 py-1.5 bg-[#FBEEEE] text-[#8A1A1A] rounded-md border-l-4 border-[#B33A3A] max-w-md"
                        data-testid="header-allergies"
                        title="Alergias registradas na AGA mais recente"
                    >
                        <AlertTriangle size={16} className="shrink-0" />
                        <div className="text-xs">
                            <p className="font-semibold uppercase tracking-wider">Alergias</p>
                            <p className="line-clamp-1">{allergies}</p>
                        </div>
                    </div>
                )}

                {/* Tags de contexto (fragilidade, pós-hosp, canabis) */}
                {tags && tags.length > 0 && (
                    <div className="flex items-center gap-1 flex-wrap" data-testid="header-tags">
                        {tags.map((t) => (
                            <span
                                key={t}
                                className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[var(--brand-accent)]/15 text-[var(--brand-accent)] border border-[var(--brand-accent)]/30"
                            >
                                {t}
                            </span>
                        ))}
                    </div>
                )}

                {/* Próxima/última consulta */}
                <div className="hidden md:flex items-center gap-4 ml-auto text-xs text-muted-clinical">
                    {last_consultation && (
                        <div>
                            <p className="uppercase tracking-widest text-[10px] text-brand-secondary font-semibold">Última</p>
                            <Link to={`/consultations/${last_consultation.id}`} className="text-brand hover:underline font-medium">
                                {fmtDate(last_consultation.scheduled_at || last_consultation.created_at)} <ChevronRight size={10} className="inline" />
                            </Link>
                        </div>
                    )}
                    {next_consultation && (
                        <div>
                            <p className="uppercase tracking-widest text-[10px] text-brand-secondary font-semibold">Próxima</p>
                            <Link to={`/consultations/${next_consultation.id}`} className="text-brand hover:underline font-medium">
                                {fmtDate(next_consultation.scheduled_at)} <ChevronRight size={10} className="inline" />
                            </Link>
                        </div>
                    )}
                </div>

                {/* Ações rápidas */}
                {isProfessional && (
                    <div className="flex items-center gap-1 ml-auto md:ml-0" data-testid="header-quick-actions">
                        {onNewConsult && (
                            <button onClick={onNewConsult} className="btn-primary text-xs px-3" data-testid="header-new-consult">
                                <PlusCircle size={14} /> Novo atendimento
                            </button>
                        )}
                        {onAttachExam && (
                            <button onClick={onAttachExam} className="btn-secondary text-xs px-3" data-testid="header-attach-exam">
                                <Upload size={14} /> Exame
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
